import { useLogger } from "@amzn/xb-hooks";
import { TypedDocumentNode, useQuery } from "@apollo/client";
import { FC } from "react";
import { LiveInterceptorFactoryInfo } from "../live-component";

type QueryVariables<T> = T extends TypedDocumentNode<unknown, infer V> ? V : never;

/**
 * PrefetchQueryData
 *
 * This type represents the data required for prefetching a GraphQL query.
 *
 * @param query - TypedDocumentNode<TResult, TVariables> - The GraphQL query to be prefetched
 * @param variables - (optional) - Variables for the GraphQL query
 * @param isBlocking - boolean (optional) - If true, shows a loader while fetching. Defaults to false
 * @param skip - boolean (optional) - If true, skips the prefetching. Defaults to false
 */
type PrefetchQueryData<TQuery extends TypedDocumentNode> = {
  query: TQuery;
  variables?: QueryVariables<TQuery>;
  isBlocking?: boolean;
  skip?: boolean;
};

export type PrefetchQueryInterface<TProps = unknown, TQuery extends TypedDocumentNode = TypedDocumentNode<any, any>> = {
  /**
   * By defining `prefetchQueryCallback` the PrefetchQueryInterceptor will prefetch and cache GraphQL
   * queries for both PageComponents and LiveComponents. This will reduce the total amount of calls to
   * the API. It additionally allows the ability to block the rendering of the entire component until the
   * query has completed.
   *
   * Note that the same props available to the LiveComponent/PageComponent will be available to this callback.
   *
   * Errors during prefetching are caught and logged, but do not affect component rendering.
   *
   * Usage:
   * Apply this interceptor when defining a page component source:
   *
   * ```
   * export default vendPageComponentSource<{ orgId:string; }>(YourPageComponent, {
   *   prefetchQueryCallback: ({orgId}) => ({
   *     query: GeneratedPrefetchDataDocument, // using the generated document ensures type safety
   *     variables: { orgId }, // optional
   *     isBlocking: true, // optional, defaults to false
   *     skip: false, // true indicates that no prefetchQuery should be made, defaults to false
   *   }),
   *   // other props
   * });
   * ```
   *
   * @param props the same props that are available to the LiveComponent/PageComponent
   * @returns PrefetchQueryData<TResult, TVariables> - the query and variables to be prefetched
   */
  prefetchQueryCallback?: (props: TProps) => PrefetchQueryData<TQuery>;
};

export const prefetchQueryInterceptorFactory = <
  TProps extends PrefetchQueryInterface<Omit<TProps, "prefetchQueryCallback">, TQuery>,
  TQuery extends TypedDocumentNode<any, any>,
>(
  { fallback = <></> }: LiveInterceptorFactoryInfo,
  Response: FC<any>
) => {
  const ResponseWithPrefetchQuery: FC<TProps> = ({ prefetchQueryCallback, ...otherProps }) => {
    const { query, variables, isBlocking, skip } = prefetchQueryCallback?.(otherProps) ?? {};
    const logger = useLogger();

    // Defining Prefetch as it's own component to avoid initiating useQuery when query was not passed
    const Prefetch = () => {
      const { loading } = useQuery(query!, {
        variables,
        fetchPolicy: "cache-first",
        onError(error) {
          logger.error("Error encountered during prefetch query", error);
        },
        onCompleted(data) {
          logger.debug("Prefetch query completed", data);
        },
        skip,
      });

      if (isBlocking && loading) {
        return <>{fallback}</>;
      } else {
        return <Response {...otherProps} />;
      }
    };

    return query ? <Prefetch /> : <Response {...otherProps} />;
  };

  return ResponseWithPrefetchQuery;
};
